import Direction2D, { Direction2DInterface } from './Direction2D';
import HalfPlane2D from './HalfPlane2D';
import Line3D from './Line3D';
import LineSegment2D from './LineSegment2D';
import Point2D, { Point2DInterface } from './Point2D';
import { centerPoint2D } from './Polygon2D';
import { distanceEquals } from './units';
import Vector2D from './Vector2D';

export interface Line2DInterface {
  readonly basePoint: Point2DInterface;
  readonly direction: Direction2DInterface;
}

class Line2D {
  static readonly xAxis = new Line2D(Point2D.origin, Direction2D.right);
  static readonly yAxis = new Line2D(Point2D.origin, Direction2D.up);

  readonly basePoint: Point2D;
  readonly direction: Direction2D;

  constructor(basePoint: Point2D, direction: Direction2D) {
    this.basePoint = basePoint;
    this.direction = direction;
    if (direction.equals(Direction2D.noDirection)) throw new Error('Line must have a direction!');
  }

  static fromObject({ basePoint, direction }: Line2DInterface) {
    return new Line2D(Point2D.fromObject(basePoint), Direction2D.fromObject(direction));
  }

  static fromLineSegment(lineSegment: LineSegment2D) {
    const direction = Direction2D.fromPoints(lineSegment.basePoint, lineSegment.endPoint);
    return new Line2D(lineSegment.basePoint, direction);
  }

  static fromPoints(basePoint: Point2D, endPoint: Point2D) {
    return new Line2D(basePoint, Direction2D.fromPoints(basePoint, endPoint));
  }

  static fromPointObjects(basePoint: { x: number; y: number }, endPoint: { x: number; y: number }) {
    return Line2D.fromPoints(Point2D.fromObject(basePoint), Point2D.fromObject(endPoint));
  }

  equals(other: Line2D) {
    const linesAreParallel = this.isParallelTo(other);
    const basePointIsOnOther = other.containsPoint(this.basePoint);
    const basePointIsOnThis = this.containsPoint(other.basePoint);

    return linesAreParallel && (basePointIsOnOther || basePointIsOnThis);
  }

  intersectWithLine(line: Line2D) {
    if (this.isParallelTo(line)) return null;

    return Line2D.intersectLines(
      this.basePoint,
      this.basePoint.add(this.direction),
      line.basePoint,
      line.basePoint.add(line.direction),
    );
  }

  /**
   * Designed with speed in mind; will return non-finite values if lines are parallel.
   * @returns The point of intersection between the lines ((x1, y1), (x2, y2)) and ((x3, y3), (x4, y4))
   */
  static intersectLines(
    { x: x1, y: y1 }: Point2DInterface,
    { x: x2, y: y2 }: Point2DInterface,
    { x: x3, y: y3 }: Point2DInterface,
    { x: x4, y: y4 }: Point2DInterface,
  ) {
    // http://mathworld.wolfram.com/Line-LineIntersection.html
    const determinant = (a: number, b: number, c: number, d: number) => a * d - b * c;

    const x1x2 = x1 - x2;
    const y1y2 = y1 - y2;
    const x3x4 = x3 - x4;
    const y3y4 = y3 - y4;

    const line1Determinant = determinant(x1, y1, x2, y2);
    const line2Determinant = determinant(x3, y3, x4, y4);

    const denominator = determinant(x1x2, y1y2, x3x4, y3y4);

    const xNumerator = determinant(line1Determinant, x1x2, line2Determinant, x3x4);
    const yNumerator = determinant(line1Determinant, y1y2, line2Determinant, y3y4);

    return new Point2D(xNumerator / denominator, yNumerator / denominator);
  }

  isParallelTo(other: { direction: Direction2D }) {
    return this.direction.isParallelTo(other.direction);
  }

  isPerpendicularTo(other: { direction: Direction2D }) {
    return this.direction.isPerpendicularTo(other.direction);
  }

  containsPoint(point: Point2D) {
    const distance = point.distanceToLine(this);
    return distanceEquals(distance, 0);
  }

  containsSegment(segment: LineSegment2D) {
    return this.containsPoint(segment.basePoint) && this.containsPoint(segment.endPoint);
  }

  getPointAlongLine(distance: number) {
    return new Point2D(this.basePoint.x + distance * this.direction.x, this.basePoint.y + distance * this.direction.y);
  }

  translate(vector: Vector2D): Line2D {
    return new Line2D(this.basePoint.translate(vector), this.direction);
  }

  reverse(): Line2D {
    return new Line2D(this.basePoint, this.direction.reverse());
  }

  isOrientedWith(direction: Direction2D) {
    return this.direction.isOrientedWith(direction);
  }

  orientWith(direction: Direction2D) {
    return this.isOrientedWith(direction) ? this : this.reverse();
  }

  to3D(z?: number): Line3D {
    return new Line3D(this.direction.to3D()!, this.basePoint.to3D(z));
  }

  toObject(): Line2DInterface {
    return { basePoint: this.basePoint.toObject(), direction: this.direction.toObject() };
  }

  getPortSideHalfPlane(): HalfPlane2D {
    return HalfPlane2D.fromDirectionAndBoundaryPoint(this.direction.rotateQuarterTurn(), this.basePoint);
  }

  rotateQuarterTurnAboutBasePoint(): Line2D {
    return new Line2D(this.basePoint, this.direction.rotateQuarterTurn());
  }

  static getSmallestAngleBisector(line1: Line2D, line2: Line2D) {
    const orientedLine2 = line2.orientWith(line1.direction);

    const direction = Direction2D.bisect(line1.direction, orientedLine2.direction);

    const perpendicularLine = new Line2D(line1.basePoint, direction.rotateQuarterTurn());
    const pointOnLine2 = perpendicularLine.intersectWithLine(line2);
    if (!pointOnLine2) throw new Error('Failed to find intersection while computing angle bisector.');

    return new Line2D(centerPoint2D([line1.basePoint, pointOnLine2]), direction);
  }
}

export default Line2D;
